import { useEffect, useMemo, useState } from 'react';

import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';

import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOnboardingChecklist } from '../hooks/coach/useOnboardingChecklist';

import { useSpinner } from '../utils/GlobalSpinner';

import { useIntl } from 'react-intl';
import { RpeTab } from '../components/coach/RpeTab';
import { CoachPlansTab } from '../components/coach/CoachPlansTab';
import { ExercisesTab } from '../components/coach/ExercisesTab';
import BankDataDialog from '../components/dialogs/BankDataDialog';
import { CoachProfileEditDialog } from '../components/coach/CoachProfileEditDialog';
import { useCoachProfileEditDialog } from '../hooks/coach/useCoachProfileEditDialog';
import { api } from '../services/api-client';
import { ICoach, IUser } from '../types/models';
import { IWorkoutTemplate } from '../types/workout/workout-template';
import { IClient } from '../types/models';
import { ESubscriptionStatus } from '../types/enums/subscription-status';
import { DEFAULT_AVATAR } from '../utils/defaultAvatar';

interface CoachInfo extends ICoach {
  profilePictureUrl?: string;
  user?: IUser;
}

const TAB_INDEX_BY_NAME: Record<string, number> = {
  exercises: 0,
  rpe: 1,
  plans: 2,
  subscription: 3
};

export default function CoachProfilePage() {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab=exercises|rpe|plans|subscription deja entrar directo al tab pedido
  // (lo usa el OnboardingChecklist para "Ver biblioteca").
  const initialIndex = useMemo<number>(() => {
    const name = searchParams.get('tab');
    if (name && name in TAB_INDEX_BY_NAME) {
      const idx = TAB_INDEX_BY_NAME[name];
      return typeof idx === 'number' ? idx : 0;
    }
    return 0;
  }, [searchParams]);
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex);

  const { user, coach } = useUser();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { setLoading } = useSpinner();
  const onboarding = useOnboardingChecklist();

  const handleTabChange = (index: number): void => {
    setActiveIndex(index);
    const name = Object.keys(TAB_INDEX_BY_NAME).find((k) => TAB_INDEX_BY_NAME[k] === index);
    if (name) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', name);
      setSearchParams(next, { replace: true });
    }
    // Si entró a la biblioteca de ejercicios, marca la tarea del checklist.
    if (index === TAB_INDEX_BY_NAME.exercises) {
      void onboarding.markLibraryViewed();
    }
  };

  // Si el usuario aterriza directamente con ?tab=exercises (desde el checklist),
  // marcamos la tarea solo en el mount. Los cambios de tab posteriores los
  // gestiona handleTabChange — evitamos doble llamada.
  useEffect(() => {
    if (initialIndex === TAB_INDEX_BY_NAME.exercises) {
      void onboarding.markLibraryViewed();
    }
    // eslint-disable-next-line
  }, []);

  // State variables
  const [refreshKey, setRefreshKey] = useState<number>(0);
  // Coach info
  const [coachInfo, setCoachInfo] = useState<CoachInfo | null>(null);

  // Data arrays
  const [users, setUsers] = useState<IClient[]>([]);
  const [totalExercises, setTotalExercises] = useState<number>(0);
  const [workouts, setWorkouts] = useState<IWorkoutTemplate[]>([]);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);

  // Modals visibility
  const [isBankDataDialogVisible, setIsBankDataDialogVisible] = useState<boolean>(false);

  const profileDialog = useCoachProfileEditDialog(coachInfo ?? coach);

  // Carga inicial crítica (datos necesarios para mostrar la página)
  useEffect(() => {
    if (!user?.userId) return;

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        // Cargar datos críticos en paralelo
        const { data } = await api.user.fetchCoach(user.userId);
        // Procesar respuesta del coach. En Fase 2 del onboarding ya no
        // forzamos /complete-coach-profile: si el coach todavía no completó
        // bio/experience/specialties, lo guía el OnboardingChecklist en /coach.
        if (data) {
          setCoachInfo(data as CoachInfo);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        showToast('error', 'Error', errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [user?.userId, coach?.id, navigate, showToast, setLoading]);

  /*
  const handleOpenBankDataDialog = () => {
    setIsBankDataDialogVisible(true);
  };
  */

  const handleBankDataDialogClose = () => {
    setIsBankDataDialogVisible(false);
    setRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      setStatsLoading(true);
      try {
        const [exercisesResult, workoutsResult, studentsResult] = await Promise.allSettled([
          api.exercise.fetchCoachExercises({ page: 1, limit: 10, search: '' }),
          api.workout.findAllWorkoutTemplatesByCoachId(),
          api.coach.fetchStudents()
        ]);

        if (exercisesResult.status === 'fulfilled') {
          setTotalExercises(exercisesResult.value.data?.total || 0);
        }
        if (workoutsResult.status === 'fulfilled') {
          setWorkouts(workoutsResult.value.data || []);
        }
        if (studentsResult.status === 'fulfilled') {
          const activeStudents =
            studentsResult.value.data?.filter(
              (student: IClient) => student.user?.subscription?.status === ESubscriptionStatus.ACTIVE
            ) || [];
          setUsers(activeStudents);
        }

        const failed = [exercisesResult, workoutsResult, studentsResult].filter((r) => r.status === 'rejected');
        if (failed.length > 0) {
          const firstError = (failed[0] as PromiseRejectedResult).reason;
          const errorMessage = firstError instanceof Error ? firstError.message : 'Unknown error';
          showToast('error', 'Error', errorMessage);
        }
      } finally {
        setStatsLoading(false);
      }
    };
    if (coach) fetchData();
  }, [refreshKey, coach?.id, showToast]);

  return (
    <div className="p-4">
      {/* Sección de cabecera del perfil */}
      <div className="surface-card shadow-2 border-round p-4 mb-4">
        <div className="flex flex-column md:flex-row align-items-center gap-4">
          <img
            src={coachInfo?.profilePictureUrl || DEFAULT_AVATAR}
            alt={coachInfo?.name || 'Coach'}
            className="border-circle w-8rem h-8rem"
            style={{ objectFit: 'cover' }}
          />
          <div className="flex-1 w-full">
            <div className="mb-3">
              <h1 className="text-4xl font-bold m-0 mb-2">{coachInfo?.name || 'Coach Profile'}</h1>
              <p className="text-600 m-0">{coachInfo?.user?.email || 'Loading...'}</p>
            </div>
            <div className="flex flex-wrap gap-4 mb-3">
              <div className="flex flex-column align-items-center">
                {statsLoading ? (
                  <Skeleton width="2.5rem" height="2rem" className="mb-1" />
                ) : (
                  <span className="text-3xl font-bold text-primary">{users.length}</span>
                )}
                <span className="text-500 text-sm">Clients</span>
              </div>
              <div className="flex flex-column align-items-center">
                {statsLoading ? (
                  <Skeleton width="2.5rem" height="2rem" className="mb-1" />
                ) : (
                  <span className="text-3xl font-bold text-primary">{workouts.length}</span>
                )}
                <span className="text-500 text-sm">Workouts</span>
              </div>
              <div className="flex flex-column align-items-center">
                {statsLoading ? (
                  <Skeleton width="2.5rem" height="2rem" className="mb-1" />
                ) : (
                  <span className="text-3xl font-bold text-primary">{totalExercises}</span>
                )}
                <span className="text-500 text-sm">Exercises</span>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                label={intl.formatMessage({ id: 'profile.edit' })}
                icon="pi pi-pencil"
                className="p-button-rounded p-button-outlined"
                onClick={() => profileDialog.open()}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal con pestañas */}
      <TabView activeIndex={activeIndex} onTabChange={(e) => handleTabChange(e.index)}>
        {/* Pestaña de Ejercicios */}
        <TabPanel header={intl.formatMessage({ id: 'coach.tabs.exercises' })} leftIcon="pi pi-heart">
          <ExercisesTab />
        </TabPanel>

        {/* Pestaña de RPE */}
        <TabPanel header={intl.formatMessage({ id: 'coach.tabs.rpe' })} leftIcon="pi pi-chart-line">
          <RpeTab />
        </TabPanel>

        {/* Pestaña de Planes */}
        <TabPanel header={intl.formatMessage({ id: 'coach.tabs.plans' })} leftIcon="pi pi-list">
          <CoachPlansTab />
        </TabPanel>

        {/*
          Tab "Suscripción" estaba en producción como teaser "¡Próximamente!".
          Se ocultó al cerrar Bug 09 del qa-audit/2026-05-21 — restaurar acá
          cuando SubscriptionTab tenga contenido real.
        */}
      </TabView>

      {/* Diálogo para crear/editar un ejercicio */}

      <BankDataDialog visible={isBankDataDialogVisible} onHide={handleBankDataDialogClose} />

      <CoachProfileEditDialog
        state={profileDialog}
        onSaved={() => {
          setRefreshKey((prev) => prev + 1);
          showToast(
            'success',
            intl.formatMessage({ id: 'common.success', defaultMessage: 'Listo' }),
            intl.formatMessage({
              id: 'coach.profileDialog.saved',
              defaultMessage: 'Tu perfil se actualizó'
            })
          );
        }}
        onError={(message) =>
          showToast('error', intl.formatMessage({ id: 'common.error', defaultMessage: 'Error' }), message)
        }
      />
    </div>
  );
}
